import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Award, Plane, Trophy, ChevronRight } from "lucide-react";

const TRIP_IMAGES = {
  Thailand: "https://images.pexels.com/photos/15917114/pexels-photo-15917114.jpeg",
  Dubai: "https://images.pexels.com/photos/36813102/pexels-photo-36813102.jpeg",
  Europe: "https://images.pexels.com/photos/32424965/pexels-photo-32424965.jpeg",
};

export default function DistRewards() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/rewards/me").then((r) => setData(r.data)); }, []);

  if (!data) return <DashboardLayout title="Rewards & Trips"><div className="text-navy/50">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout title="Rewards & Trips" subtitle="Climb the ranks. Travel the world.">
      {/* Current rank */}
      <div className="bg-navy text-white p-8 rounded-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-gold/10 rounded-full blur-3xl" />
        <div className="relative flex flex-wrap gap-8 items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-gold mb-3">Your current rank</div>
            <div className="flex items-center gap-4">
              <Trophy className="text-gold" size={40} />
              <div>
                <div className="font-serif text-5xl">{data.current_rank.label}</div>
                <div className="text-xs text-white/50 mt-1">Earned reward: ₹{(data.current_rank.reward || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gold">Lifetime Left SP</div>
              <div className="font-serif text-3xl mt-1 tabular-nums lining-nums">{data.cum_left_sp?.toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gold">Lifetime Right SP</div>
              <div className="font-serif text-3xl mt-1 tabular-nums lining-nums">{data.cum_right_sp?.toLocaleString("en-IN")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress to next rank */}
      {data.progress && (
        <div className="mt-6 bg-white border border-border p-6 rounded-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-serif text-xl text-navy">Next rank: {data.progress.next_rank.label}</h3>
            <div className="text-sm text-gold-deep">+₹{(data.progress.next_rank.reward || 0).toLocaleString("en-IN")} reward</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between text-xs text-navy/60 mb-2">
                <span>Strong leg</span>
                <span>{data.progress.strong_leg_bv?.toLocaleString("en-IN")} / {data.progress.next_rank.min_left?.toLocaleString("en-IN")} SP</span>
              </div>
              <div className="h-2 bg-cream rounded-sm overflow-hidden">
                <div data-testid="progress-strong" className="h-full bg-olive" style={{ width: `${data.progress.strong_pct}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-navy/60 mb-2">
                <span>Weak leg</span>
                <span>{data.progress.weak_leg_bv?.toLocaleString("en-IN")} / {data.progress.next_rank.min_right?.toLocaleString("en-IN")} SP</span>
              </div>
              <div className="h-2 bg-cream rounded-sm overflow-hidden">
                <div data-testid="progress-weak" className="h-full bg-rose" style={{ width: `${data.progress.weak_pct}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trips */}
      <div className="mt-8">
        <h3 className="font-serif text-2xl text-navy mb-4">Reward Trips</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {data.all_trips.map((t) => {
            const qualified = (data.trips_qualified || []).includes(t.name);
            return (
              <div key={t.name} data-testid={`trip-${t.name}`} className={`relative aspect-[4/5] overflow-hidden rounded-sm ${qualified ? "ring-2 ring-gold" : ""}`}>
                <img src={TRIP_IMAGES[t.name]} alt={t.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-2">
                    <Plane size={12} /> Level {t.level}
                  </div>
                  <div className="font-serif text-3xl text-white">{t.name}</div>
                  <div className="text-xs text-white/60 mt-1">Req: {t.left_sp.toLocaleString("en-IN")} / {t.right_sp.toLocaleString("en-IN")} SP</div>
                  <div className={`mt-3 inline-flex text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${qualified ? "bg-gold text-navy" : "bg-white/10 text-white/60"}`}>
                    {qualified ? "Qualified" : "Locked"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
